import React, { useCallback, useEffect, useState } from 'react';
import { Button, Flex, Form, Tooltip } from 'antd';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import editIcon from 'assets/images/table-icons/edit_green.svg';
import pdfIcon from 'assets/images/icons/pdf_red.svg';
import pdfIconDisable from 'assets/images/icons/pdf_white.svg';
import { debounce } from 'lodash';

/**
 * ActionButtons component handles all action buttons for the lease management system
 * with flexibility to work in both Create and Edit modes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {function} props.handleAddRow - Function to handle adding a new row
 * @param {function} props.handlePreview - Function to handle preview/schedule generation
 * @param {function} [props.handleCreateClick] - Function for Create mode submission
 * @param {function} [props.handleUpdateButtonActions] - Function for Edit mode submission
 * @param {boolean} props.isLoadDataButtonDisabled - Flag to determine if Load Data button should be disabled
 * @param {boolean} [props.isPopulateScheduleDisabled] - Create mode: flag for preview button
 * @param {boolean} [props.newRowAdded] - Edit mode: flag that enables preview when true
 * @param {boolean} [props.isSubmitButtonDisable] - Create mode: flag for submit button
 * @param {boolean} [props.isUpdateButtonDisabled] - Edit mode: flag for update button
 * @param {boolean} props.isScheduleLoading - Flag to indicate if schedule is loading
 * @param {Object} props.viewData - View data for the lease record (needed for VIEW mode)
 * @param {Object} props.state - Current application state object
 * @param {function} props.navigate - Navigation function for routing
 * @param {function} props.handleSingleLeaseDeactivate - Function to handle lease deactivation
 * @param {boolean} props.areAllRecordsRecorded - Flag to check if all records are recorded
 * @param {function} props.isLeaseLifeCycleOver - Function to check if lease lifecycle is over
 * @param {Object} [props.selectHistory] - Function to select history record
 *
 * @returns {JSX.Element} The action buttons section
 */
const ActionButtons = ({
	screen,
	// handleAddRow,
	handlePreview,
	// isLoadDataClick,
	// setIsLoadDataClick,
	handleCreateClick,
	handleUpdateButtonActions,
	// isLoadDataButtonDisabled,
	// isPopulateScheduleDisabled,
	newRowAdded,
	// isSubmitButtonDisable,
	isUpdateButtonDisabled,
	isScheduleLoading,
	viewData,
	state,
	navigate,
	handleSingleLeaseDeactivate,
	areAllRecordsRecorded,
	isLeaseLifeCycleOver,
	selectHistory,
}) => {
	const form = Form.useFormInstance();
	const theme = useTheme();

	const [isLoadDataButtonDisabled, setIsLoadDataButtonDisabled] = useState(true);
	const [isPopulateScheduleDisabled, setIsPopulateScheduleDisabled] = useState(true);
	const [isSubmitButtonDisable, setIsSubmitButtonDisable] = useState(true);

	const leaseType = Form.useWatch('leaseType', form);
	const leaseTitle = Form.useWatch('leaseTitle', form);
	const lessorName = Form.useWatch('lessorName', form);
	const initialDirectCost = Form.useWatch('initialDirectCost', form);
	const upfrontLeasePayment = Form.useWatch('upfrontLeasePayment', form);
	const leaseTerm = Form.useWatch('leaseTerm', form);
	const grossRent = Form.useWatch('grossRent', form);
	const initialDiscountRate = Form.useWatch('initialDiscountRate', form);
	const escalationPercentage = Form.useWatch('escalationPercentage', form);
	const advanceAmount = Form.useWatch('advanceAmount', form);
	const advanceDuration = Form.useWatch('advanceDuration', form);
	const rightOfUseAssetValue = Form.useWatch('rightOfUseAssetValue', form);
	const endOfFinancialYear = Form.useWatch('endOfFinancialYear', form);
	const securityDeposit = Form.useWatch('securityDeposit', form);

	const handleAddRow = useCallback(() => {
		const currentValues = {
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentTime: form.getFieldValue('paymentModality'),
			inputChangeLeaseTerm: form.getFieldValue('leaseTerm'),
			inputChangeGrossAmount: form.getFieldValue('grossRent'),
			inputChangeDiscountRate: form.getFieldValue('initialDiscountRate'),
			inputChangeEscalation: form.getFieldValue('escalationPercentage'),
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAmortizationPeriod: form.getFieldValue('advanceDuration'),
			inputChangeEffectiveDate: form.getFieldValue('leaseStartDate'),
		};

		if (screen === 'CREATE') {
			// form.setFieldsValue({ changes: { ...currentValues } });
			form.setFieldsValue({ changes: [currentValues] });
			setIsLoadDataButtonDisabled(true);
		}
		// Update the form values
		const changeTableDataFillled = isChangedTabledataFilled();

		if (changeTableDataFillled) {
			setIsPopulateScheduleDisabled(false);
		}
		const isSubmitDisabled = isSubmitButtonDisabledFunc(true);

		// console.log('isSubmitDisabled', isSubmitDisabled);

		setIsSubmitButtonDisable(isSubmitDisabled);
	}, [form, screen]);

	// Determine the appropriate handlers and flags based on screen mode
	const handleSubmitAction = () => {
		if (screen === 'CREATE') {
			if (handleCreateClick) handleCreateClick(true);
		} else {
			if (handleUpdateButtonActions) handleUpdateButtonActions();
		}
	};

	const isSubmitButtonDisabledFunc = useCallback((loadDataClicked) => {
		// 1. Check if required financial fields are filled

		if (!isRequiredfieldsFilled()) {
			// console.log('isRequiredfieldsFilled');

			return true;
		}

		// 2. Check if changes table data is filled
		if (!isChangedTabledataFilled()) {
			// console.log('isChangedTabledataFilled');
			return true;
		}

		// 3. Check if data is consistent between form and changes table
		if (!isFirstRowMatchWithRequiredFields()) {
			// console.log('isFirstRowMatchWithRequiredFields');
			return true;
		}
		if (!isOtherMendatoryFieldsFilled()) {
			// console.log('isOtherMendatoryFieldsFilled');
			return true;
		}

		// 4. Check if lease schedule data has been generated (button was clicked)
		if (
			!loadDataClicked
			// || !leaseControlData || leaseControlData.length === 0
		) {
			// console.log('loadDataClicked');
			return true;
		}

		// All validations passed
		return false;
	}, []);

	const loadDataButtonDisabilityCheck = () => {
		if (isRequiredfieldsFilled()) {
			if (isChangedTabledataFilled()) {
				if (isFirstRowMatchWithRequiredFields()) {
					return true;
				} else {
					return false;
				}
			} else {
				return false;
			}
		} else {
			return true;
		}
	};

	const isChangedTabledataFilled = () => {
		const changes = (form.getFieldValue('changes') ?? [])[0];

		if (
			// changes.inputChangeAdvance &&
			changes?.inputChangeAmortizationPeriod &&
			changes?.inputChangeDiscountRate &&
			changes?.inputChangeEffectiveDate &&
			changes?.inputChangeEscalation &&
			changes?.inputChangeGrossAmount &&
			changes?.inputChangeLeaseTerm &&
			changes?.paymentFrequency &&
			changes?.paymentTime
		) {
			return true;
		}
		return false;
	};

	const isFirstRowMatchWithRequiredFields = () => {
		const changes = (form.getFieldValue('changes') ?? [])[0];
		if (
			changes?.inputChangeLeaseTerm === form.getFieldValue('leaseTerm') &&
			changes?.paymentFrequency === form.getFieldValue('paymentFrequency') &&
			changes?.paymentTime === form.getFieldValue('paymentModality') &&
			changes?.inputChangeGrossAmount === form.getFieldValue('grossRent') &&
			changes?.inputChangeEscalation === form.getFieldValue('escalationPercentage') &&
			changes?.inputChangeDiscountRate === form.getFieldValue('initialDiscountRate')
		) {
			return true;
		}
		return false;
	};

	const isOtherMendatoryFieldsFilled = () => {
		const leaseType = form.getFieldValue('leaseType');
		const leaseTitle = form.getFieldValue('leaseTitle');
		const lessorName = form.getFieldValue('lessorName');
		const upfrontLeasePayment = form.getFieldValue('upfrontLeasePayment');
		const initialDirectCost = form.getFieldValue('initialDirectCost');
		const securityDeposit = form.getFieldValue('securityDeposit');
		const endOfFinancialYear = form.getFieldValue('endOfFinancialYear');
		const rightOfUseAssetValue = form.getFieldValue('rightOfUseAssetValue');
		if (
			!leaseType ||
			!leaseTitle ||
			!lessorName ||
			!upfrontLeasePayment ||
			!initialDirectCost ||
			!securityDeposit ||
			!endOfFinancialYear ||
			!rightOfUseAssetValue
		) {
			return false;
		}
		return true;
	};

	const isRequiredfieldsFilled = () => {
		const leaseTerm = form.getFieldValue('leaseTerm');
		const grossRent = form.getFieldValue('grossRent');
		const initialDiscountRate = form.getFieldValue('initialDiscountRate');
		const escalationPercentage = form.getFieldValue('escalationPercentage');
		const advanceAmount = form.getFieldValue('advanceAmount');
		const advanceDuration = form.getFieldValue('advanceDuration');

		if (!leaseTerm || !grossRent || !initialDiscountRate || !escalationPercentage || !advanceAmount || !advanceDuration) {
			return false;
		}

		return true;
	};

	// useEffect(() => {
	// 	const isSubmitDisabled = isSubmitButtonDisabledFunc();
	// 	setIsSubmitButtonDisable(isSubmitDisabled);
	// }, [isLoadDataClick]);

	useEffect(() => {
		const handleFormValueChanges = debounce(() => {
			const isDisabled = loadDataButtonDisabilityCheck();
			setIsLoadDataButtonDisabled(isDisabled);
			const isSubmitDisabled = isSubmitButtonDisabledFunc(true);
			setIsSubmitButtonDisable(isSubmitDisabled);
		}, 600);

		handleFormValueChanges();
	}, [
		rightOfUseAssetValue,
		endOfFinancialYear,
		securityDeposit,
		initialDirectCost,
		upfrontLeasePayment,
		lessorName,
		leaseTitle,
		leaseType,
		leaseTerm,
		grossRent,
		initialDiscountRate,
		escalationPercentage,
		advanceAmount,
		advanceDuration,
	]);

	// Determine button disabled states based on screen mode
	const isPreviewButtonDisabled = screen === 'CREATE' ? isPopulateScheduleDisabled : !newRowAdded;

	const isSubmitButtonDisabled = screen === 'CREATE' ? isSubmitButtonDisable : isUpdateButtonDisabled;
	// const isSubmitButtonDisabled = false;

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', pt: '15px' }}>
			{screen !== 'VIEW' ? (
				<>
					{/* Left side buttons */}
					<Flex justify="left" gap={9}>
						<Tooltip title={isLoadDataButtonDisabled ? 'Required fields are unchanged' : ''}>
							<Button
								disabled={isLoadDataButtonDisabled}
								type="primary"
								onClick={handleAddRow}
								style={{ marginBottom: 10, marginTop: 10 }}
							>
								Load Data
							</Button>
						</Tooltip>
						<Button
							disabled={isPreviewButtonDisabled}
							type="primary"
							onClick={handlePreview}
							style={{ marginBottom: 10, marginTop: 10 }}
							loading={isScheduleLoading}
						>
							{screen === 'CREATE' ? 'Run Lease Schedule' : 'Update Lease Schedule'}
						</Button>
					</Flex>

					{/* Right side button */}
					<Button
						disabled={isSubmitButtonDisabled}
						type="primary"
						onClick={handleSubmitAction}
						style={{
							backgroundColor: isSubmitButtonDisabled ? '#b4bdbb' : theme.palette.lynTheme.main,
							marginBottom: 10,
							marginTop: 10,
							cursor: isSubmitButtonDisabled ? 'not-allowed' : 'pointer',
						}}
					>
						{screen === 'CREATE' ? 'Submit' : 'Update'}
					</Button>
				</>
			) : (
				// VIEW mode buttons
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
					{viewData && (
						<>
							<Button
								type="primary"
								style={{
									fontSize: '13px',
									fontWeight: '500',
									borderRadius: '6px',
									marginRight: '3px',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									...(viewData?.history?.length === 0 ? { backgroundColor: '#6c757d' } : {}),
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (selectHistory) {
										selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
									} else if (viewData?.selectHistory) {
										viewData.selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
									}
								}}
							>
								<img src={viewData?.history?.length > 1 ? pdfIcon : pdfIconDisable} width={16} height={16} alt="view" />
								Change Log
							</Button>
							<Button
								disabled={viewData?.activationStatus?.toLowerCase() === 'closed'}
								style={{
									display: 'flex',
									alignItems: 'center',
									fontSize: '13px',
									fontWeight: '500',
									borderRadius: '6px',
									margin: '1px',
									marginLeft: '6px',
								}}
								icon={<img src={editIcon} width={16} height={16} alt="edit" />}
								onClick={(e) => {
									e.stopPropagation();
									if (navigate && state) {
										navigate('/static/lease-record-management/edit', {
											state: state,
										});
									}
								}}
							>
								Edit
							</Button>

							{areAllRecordsRecorded &&
								isLeaseLifeCycleOver &&
								isLeaseLifeCycleOver() &&
								(viewData?.activationStatus !== 'Closed' ? (
									<Button
										style={{
											display: 'flex',
											alignItems: 'center',
											fontSize: '13px',
											fontWeight: '500',
											borderRadius: '6px',
											margin: '1px',
											marginLeft: '6px',
										}}
										onClick={(e) => {
											e.stopPropagation();
											if (handleSingleLeaseDeactivate) {
												handleSingleLeaseDeactivate('Closed');
											}
										}}
									>
										Closed
									</Button>
								) : (
									<Button
										style={{
											display: 'flex',
											alignItems: 'center',
											fontSize: '13px',
											fontWeight: '500',
											borderRadius: '6px',
											margin: '1px',
											marginLeft: '6px',
										}}
										onClick={(e) => {
											e.stopPropagation();
											if (handleSingleLeaseDeactivate) {
												handleSingleLeaseDeactivate('Reopened');
											}
										}}
									>
										Reopen
									</Button>
								))}
						</>
					)}
				</Box>
			)}
		</Box>
	);
};

export default ActionButtons;
